import type { ApiClient } from '../client/api-client';
import { createSecurityService, type SecurityService } from './security-service';
import { createRbacService, type RbacService } from './rbac-service';
import { createComplianceService, type ComplianceService } from './compliance-service';
import { createAmlService, type AmlService } from './aml-service';
import { createWorkspaceService, type WorkspaceService } from './workspace-service';
import type { AuthResponseDTO } from '../contracts/auth';
import type * as Contracts from '../contracts/index';
import type { SecondFactor, WithdrawalAddressState } from '../contracts/security';
import type {
  BackendLimitChangeDto,
  BackendLimitsDto,
  BackendPolicyDecisionDto,
  BackendStopDto,
} from '../contracts/withdrawal-policy';
import { mapLimitChanges, mapPolicyDecisions, mapStops, mapWithdrawalLimits } from './map-withdrawal-policy';
import {
  buildWalletAddressesMonitoringQuery,
  buildWalletMonitoringQuery,
} from './wallet-monitoring-query';
import { createReconciliationService } from './reconciliation-service';

export function createAccountingService(client: ApiClient) {
  return {
    getBalance: () => client.get<Contracts.BalanceDto>('/app/v1/balances'),
    getAccountings: (params: Contracts.PaginationParams) =>
      client.get<Contracts.PaginatedAccountingsResponse>(
        `/app/v1/balances/accountings?page=${params.page || 1}&limit=${params.limit || 20}`
      ),
    getServiceAccountings: (params: Contracts.ServiceAccountingsParams) => {
      const search = new URLSearchParams();
      search.set('page', String(params.page || 1));
      search.set('limit', String(params.limit || 35));
      if (params.search) search.set('search', params.search);
      if (params.dateFrom) search.set('dateFrom', params.dateFrom);
      if (params.dateTo) search.set('dateTo', params.dateTo);
      if (params.types) search.set('types', params.types);
      if (params.walletKinds) search.set('walletKinds', params.walletKinds);
      if (params.sort) search.set('sort', params.sort);
      return client.get<Contracts.ServiceAccountingsResponse>(
        `/app/v1/balances/accountings/service?${search.toString()}`
      );
    },
    getOperationComments: (operationId: string) =>
      client.get<Contracts.ServiceAccountingCommentDto[]>(
        `/app/v1/balances/accountings/service/${encodeURIComponent(operationId)}/comments`
      ),
    addOperationComment: (operationId: string, text: string) =>
      client.post<Contracts.ServiceAccountingCommentDto>(
        `/app/v1/balances/accountings/service/${encodeURIComponent(operationId)}/comments`,
        { text }
      ),
  };
}

export function createAddressAmlService(client: ApiClient) {
  const buildQuery = (params: Contracts.AddressAmlListParams | Contracts.AddressCatalogListParams) => {
    const search = new URLSearchParams();
    search.set('page', String(params.page ?? 1));
    search.set('limit', String(params.limit ?? 50));
    if ('q' in params && params.q) search.set('q', params.q);
    if ('sort' in params && params.sort) search.set('sort', String(params.sort));
    if ('order' in params && params.order) search.set('order', String(params.order));
    return search.toString();
  };

  return {
    createAddressAml: (payload: Contracts.CreateAddressAmlPayload) =>
      client.post<{ addressAml: Contracts.AddressAmlDto } | Contracts.AddressAmlDto>(
        '/app/v1/address-aml',
        payload
      ),
    getAddressAmls: (params: Contracts.AddressAmlListParams = {}) =>
      client.get<Contracts.ListItemsResponse<Contracts.AddressAmlDto>>(
        `/app/v1/address-aml?${buildQuery(params)}`
      ),
    deleteAddressAml: (id: string | number) =>
      client.delete<{ addressAml?: Contracts.AddressAmlDto } | void>(`/app/v1/address-aml/${id}`),
    toggleAddressAmlActive: (id: string | number) =>
      client.patch<{ addressAml: Contracts.AddressAmlDto } | Contracts.AddressAmlDto>(
        `/app/v1/address-aml/${id}/toggle-active`
      ),
    getAddresses: (params: Contracts.AddressCatalogListParams = {}) =>
      client.get<Contracts.ListItemsResponse<Contracts.AddressCatalogDto>>(
        `/app/v1/address?${buildQuery(params)}`
      ),
  };
}

export function createAuthService(client: ApiClient) {
  return {
    register: (payload: Contracts.RegistrationPayload) =>
      client.post<AuthResponseDTO>('/app/v1/registration', payload),
    login: (payload: Contracts.LoginPayload) =>
      client.post<AuthResponseDTO>('/app/v1/auth/login', payload),
    loginConfirm: (payload: Contracts.LoginConfirmPayload) =>
      client.post<AuthResponseDTO>('/app/v1/auth/login-confirm', payload),
    checkEmail: (token: string) =>
      client.post<AuthResponseDTO>(`/app/v1/auth/check-email/${token}`),
    logout: () =>
      client.post<void>('/app/v1/auth/logout', {}),
    sendEmail: () =>
      client.post<void>('/app/v1/auth/send-email'),
    getUser: () =>
      client.get<Contracts.UserDto>('/app/v1/users/me'),
    refreshReferralCode: () =>
      client.patch<{ referralCode: string }>('/app/v1/users/me/refresh-referral'),
    enable2fa: () =>
      client.post<{ secret: string; qrCode: string }>('/app/v1/users/enable2fa'),
    enable2faConfirm: (payload: { code: string }) =>
      client.post<{ enabled: boolean }>('/app/v1/users/enable2fa-confirm', payload),
    refreshApiToken: (payload: unknown = {}) =>
      client.post<{ apiToken: string }>('/app/v1/users/me/refresh-api-token', payload),
  };
}

export function createInvoicesService(client: ApiClient) {
  return {
    getInvoices: (params: Contracts.PaginationParams) =>
      client.get<Contracts.PaginatedResponse<Contracts.InvoiceDto>>(
        `/app/v1/invoices?page=${params.page || 1}&limit=${params.limit || 10}`
      ),
    /** Public pay page — GET /api/v1/invoices/:id */
    getInvoice: (id: string) =>
      client.get<Contracts.InvoiceDto>(`/api/v1/invoices/${id}`),
    /** Force check = refresh GET (legacy «Проверить транзакцию») */
    checkInvoice: (id: string) =>
      client.get<Contracts.InvoiceDto>(`/api/v1/invoices/${id}`),
    getInvoiceStats: () =>
      client.get<Contracts.InvoiceStatsDto>('/app/v1/invoices/statistic'),
    createInvoice: (payload: Contracts.CreateInvoicePayload) =>
      client.post<Contracts.InvoiceDto>('/app/v1/invoices', payload),
  };
}

export function createIpWhitelistService(client: ApiClient) {
  const buildQuery = (params: Contracts.IpWhitelistListParams = {}) => {
    const search = new URLSearchParams();
    search.set('page', String(params.page ?? 1));
    search.set('limit', String(params.limit ?? 10));
    return search.toString();
  };

  return {
    getIpWhitelists: (params: Contracts.IpWhitelistListParams = {}) =>
      client.get<Contracts.IpWhitelistDto[] | Contracts.ListItemsResponse<Contracts.IpWhitelistDto>>(
        `/app/v1/ip-whitelists?${buildQuery(params)}`
      ),
    createIpWhitelist: (payload: Contracts.CreateIpWhitelistPayload) =>
      client.post<Contracts.IpWhitelistDto>('/app/v1/ip-whitelists', payload),
    deleteIpWhitelist: (id: string | number) =>
      client.delete<Contracts.IpWhitelistDto | void>(`/app/v1/ip-whitelists/${id}`),
  };
}

export function createMetaService(client: ApiClient) {
  return {
    getCurrencies: () =>
      client.get<Contracts.CurrencyDto[]>('/app/v1/meta/currencies'),
    getPartnersCount: () =>
      client.get<Contracts.PartnersCountDto>('/app/v1/meta/partners-count'),
  };
}

export function createProjectsService(client: ApiClient) {
  return {
    getProjects: () =>
      client.get<Contracts.ProjectDto[]>('/app/v1/projects'),
    getProject: (id: string) =>
      client.get<Contracts.ProjectDto>(`/app/v1/projects/${id}`),
    createProject: (payload: Contracts.CreateProjectPayload) =>
      client.post<Contracts.ProjectDto>('/app/v1/projects', payload),
    editProject: (id: string, payload: Contracts.UpdateProjectPayload) =>
      client.patch<Contracts.ProjectDto>(`/app/v1/projects/${id}`, payload),
    deleteProject: (id: string) =>
      client.delete<void>(`/app/v1/projects/${id}`),
    updateProjectIpWhitelist: (id: string, ips: string[]) =>
      client.put<void>(`/app/v1/projects/${id}/ip-whitelist`, { ips }),
  };
}

export function createReferralService(client: ApiClient) {
  return {
    getPartnersBalance: () =>
      client.get<Contracts.PartnersBalanceDto>('/app/v1/balances/partners'),
    getPartnersAccountings: (params: Contracts.PaginationParams) =>
      client.get<Contracts.PaginatedAccountingsResponse>(
        `/app/v1/balances/accountings/partners?page=${params.page || 1}&limit=${params.limit || 20}`
      ),
  };
}

export function createWalletsService(client: ApiClient) {
  const buildLegacyWalletsQuery = (params: Contracts.WalletsListParams) => {
    const search = new URLSearchParams();
    search.set('page', String(params.page ?? 1));
    search.set('limit', String(params.limit ?? 50));
    if (params.walletGroupId != null && params.walletGroupId !== '') {
      search.set('walletGroupId', String(params.walletGroupId));
    }
    if (params.q) search.set('q', params.q);
    return search.toString();
  };

  return {
    /** Legacy list for accounting and other screens — only page/limit/q/groupId. */
    getWallets: (params: Contracts.WalletsListParams = {}) =>
      client.get<Contracts.PaginatedResponse<Contracts.WalletDto>>(
        `/app/v1/wallets?${buildLegacyWalletsQuery(params)}`,
      ),
    getWalletGroups: () =>
      client.get<Contracts.WalletGroupsMonitoringResponse>('/app/v1/wallets/groups'),
    getWalletsMonitoring: (params: Contracts.WalletsMonitoringQuery = {}) =>
      client.get<Contracts.WalletsMonitoringResponse>(
        `/app/v1/wallets?${buildWalletMonitoringQuery(params)}`,
      ),
    getWalletAddresses: (
      walletId: number | string,
      params: Contracts.WalletAddressesMonitoringQuery = {},
    ) =>
      client.get<Contracts.WalletAddressesMonitoringResponse>(
        `/app/v1/wallets/${encodeURIComponent(String(walletId))}/addresses?${buildWalletAddressesMonitoringQuery(params)}`,
      ),
    changeWalletAddressControlMode: (
      addressId: number | string,
      payload: Contracts.ChangeAddressControlModePayload,
    ) =>
      client.patch<Contracts.WalletMonitoringAddress>(
        `/app/v1/wallets/addresses/${encodeURIComponent(String(addressId))}/control-mode`,
        payload,
      ),
    setWalletAddressAmlMonitoring: (addressId: number | string, enabled: boolean) =>
      client.put<Contracts.WalletMonitoringAddress>(
        `/app/v1/wallets/addresses/${encodeURIComponent(String(addressId))}/aml-monitoring`,
        { enabled },
      ),
    getWallet: (id: string) =>
      client.get<Contracts.WalletMonitoringItem>(`/app/v1/wallets/${id}`),
    createOperation: (payload: Contracts.CreateAccountingOperationPayload) =>
      client.post<Contracts.ServiceAccountingOperationDto>('/app/v1/balances/accountings', payload),
  };
}

export function createAuditLogService(client: ApiClient) {
  return {
    list: (search: string) =>
      client.get<Contracts.AuditLogPageDto>(`/app/v1/audit-logs?${search}`),
    getById: (id: string) =>
      client.get<Contracts.AuditLogEventDto>(`/app/v1/audit-logs/${encodeURIComponent(id)}`),
    exportCsv: (search: string) =>
      client.getBlob(`/app/v1/audit-logs/export${search ? `?${search}` : ''}`),
  };
}

export function createWithdrawalsService(client: ApiClient) {
  const buildWithdrawalsQuery = (params: Contracts.WithdrawalsListParams) => {
    const search = new URLSearchParams();
    search.set('page', String(params.page ?? 1));
    search.set('limit', String(params.limit ?? 20));
    search.set('status', params.status ?? '');
    if (params.state) search.set('state', params.state);
    return search.toString();
  };

  return {
    getWithdrawals: (params: Contracts.WithdrawalsListParams = {}) =>
      client.get<Contracts.PaginatedResponse<Contracts.WithdrawalUserDto>>(
        `/app/v1/withdrawals?${buildWithdrawalsQuery(params)}`
      ),
    cancelWithdrawal: (id: string | number) =>
      client.patch<Contracts.WithdrawalUserDto>(`/app/v1/withdrawals/${id}/cancel`),
    createPartnersWithdrawal: (payload: Contracts.CreateWithdrawalPayload) =>
      client.post<Contracts.WithdrawalUserDto>('/app/v1/withdrawals/partners', payload),
    createMerchantsWithdrawal: (payload: Contracts.CreateWithdrawalPayload) =>
      client.post<Contracts.WithdrawalUserDto>('/app/v1/withdrawals/merchants', payload),
    getAdminWithdrawals: (params: Contracts.WithdrawalsListParams = {}) =>
      client.get<Contracts.PaginatedResponse<Contracts.WithdrawalAdminDto>>(
        `/app/v1/withdrawals/admin?${buildWithdrawalsQuery(params)}`
      ),
    cancelAdminWithdrawal: (id: string | number, payload: Contracts.CancelAdminWithdrawalPayload) =>
      client.patch<Contracts.WithdrawalAdminDto>(`/app/v1/withdrawals/admin/${id}/cancel`, {
        comment: payload.comment,
        reason: payload.comment,
      }),
    processAdminWithdrawal: async (id: string | number, payload: Contracts.ApproveWithdrawalPayload = {}) => {
      const challengeId =
        payload.challengeId ??
        (
          await client.post<{ challengeId: string }>(`/app/v1/withdrawals/${encodeURIComponent(String(id))}/approval-challenges`, {
            ...(payload.forceCoolingPeriod
              ? { forceCoolingPeriod: true, forceReason: payload.forceReason }
              : {}),
            ...(payload.emergencyGrantId ? { emergencyGrantId: payload.emergencyGrantId } : {}),
          })
        ).challengeId;
      return client.post<Contracts.WithdrawalAdminDto>(`/app/v1/withdrawals/admin/${encodeURIComponent(String(id))}`, {
        challengeId,
        secondFactor: payload.secondFactor,
      });
    },
    createAutoWithdrawal: (payload: Contracts.AutoWithdrawalPayload) =>
      client.post<Contracts.AutoWithdrawalSaveResponse>('/app/v1/withdrawals/auto', payload),
    updateAutoWithdrawal: (payload: Contracts.AutoWithdrawalPayload) =>
      client.put<Contracts.AutoWithdrawalSaveResponse>('/app/v1/withdrawals/auto', payload),
    createIntent: (payload: Contracts.WithdrawalIntentPayload, idempotencyKey: string) =>
      client.post<Contracts.WithdrawalIntentDto>('/app/v1/withdrawals/intents', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    confirmMerchantsWithdrawal: (payload: Contracts.ConfirmWithdrawalPayload, idempotencyKey: string) =>
      client.post<Contracts.WithdrawalUserDto>('/app/v1/withdrawals/merchants', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    confirmPartnersWithdrawal: (payload: Contracts.ConfirmWithdrawalPayload, idempotencyKey: string) =>
      client.post<Contracts.WithdrawalUserDto>('/app/v1/withdrawals/partners', payload, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    getWithdrawal: (id: string | number) =>
      client.get<Contracts.WithdrawalUserDto>(`/app/v1/withdrawals/${encodeURIComponent(String(id))}`),
    listStateEvents: (id: string | number) =>
      client.get<Contracts.WithdrawalStateEventDto[] | { data: Contracts.WithdrawalStateEventDto[] }>(
        `/app/v1/withdrawals/${encodeURIComponent(String(id))}/state-events`,
      ),
    listAdminStateEvents: (id: string | number) =>
      client.get<Contracts.WithdrawalStateEventDto[] | { data: Contracts.WithdrawalStateEventDto[] }>(
        `/app/v1/admin/withdrawals/${encodeURIComponent(String(id))}/state-events`,
      ),
    reconcileWithdrawal: (id: string | number, payload: Contracts.ReconcileWithdrawalPayload) =>
      client.post(`/app/v1/admin/withdrawals/${encodeURIComponent(String(id))}/reconcile`, payload),
    getLimits: async () => mapWithdrawalLimits(await client.get<BackendLimitsDto>('/app/v1/withdrawals/limits')),
    getMerchantLimits: async (merchantId: string) =>
      mapWithdrawalLimits(
        await client.get<BackendLimitsDto>(
          `/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/withdrawal-limits`,
        ),
      ),
    proposeLimitChange: (merchantId: string, payload: Contracts.WithdrawalLimitChangePayload) =>
      client.post<Contracts.WithdrawalLimitChangeDto>(
        `/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/withdrawal-limit-changes`,
        payload,
      ),
    listLimitChanges: async () =>
      mapLimitChanges(
        await client.get<BackendLimitChangeDto[] | { data: BackendLimitChangeDto[] }>(
          '/app/v1/admin/withdrawal-limit-changes',
        ),
      ),
    approveLimitChange: (id: string, secondFactor: Contracts.WithdrawalLimitChangePayload['secondFactor']) =>
      client.post(`/app/v1/admin/withdrawal-limit-changes/${encodeURIComponent(id)}/approve`, { secondFactor }),
    rejectLimitChange: (id: string) =>
      client.post(`/app/v1/admin/withdrawal-limit-changes/${encodeURIComponent(id)}/reject`, {}),
    cancelLimitChange: (id: string) =>
      client.post(`/app/v1/admin/withdrawal-limit-changes/${encodeURIComponent(id)}/cancel`, {}),
    listStops: async () =>
      mapStops(await client.get<BackendStopDto[] | { data: BackendStopDto[] }>('/app/v1/admin/withdrawal-stops')),
    createStop: async (payload: Contracts.CreateWithdrawalStopPayload, idempotencyKey: string) =>
      mapStops([
        await client.post<BackendStopDto>('/app/v1/admin/withdrawal-stops', payload, {
          headers: { 'Idempotency-Key': idempotencyKey },
        }),
      ])[0],
    releaseStop: async (id: string, reason: string | undefined, idempotencyKey: string) =>
      mapStops([
        await client.post<BackendStopDto>(
          `/app/v1/admin/withdrawal-stops/${encodeURIComponent(id)}/release`,
          reason ? { reason } : {},
          { headers: { 'Idempotency-Key': idempotencyKey } },
        ),
      ])[0],
    getPolicySettings: () =>
      client.get<Contracts.WithdrawalPolicySettingsDto>('/app/v1/admin/withdrawal-policy-settings'),
    updatePolicySettings: (payload: Contracts.UpdateWithdrawalPolicySettingsPayload) =>
      client.patch<Contracts.WithdrawalPolicySettingsDto>('/app/v1/admin/withdrawal-policy-settings', payload),
    getPolicyDecisions: async (id: string) =>
      mapPolicyDecisions(
        await client.get<BackendPolicyDecisionDto[] | { data: BackendPolicyDecisionDto[] }>(
          `/app/v1/admin/withdrawals/${encodeURIComponent(id)}/policy-decisions`,
        ),
      ),
    requestApprovalChallenge: (
      id: string,
      body: { forceCoolingPeriod?: boolean; forceReason?: string; emergencyGrantId?: string },
    ) =>
      client.post<{ challengeId: string; expiresAt?: string }>(
        `/app/v1/withdrawals/${encodeURIComponent(id)}/approval-challenges`,
        body,
      ),
    sendIntentEmailOtp: (intentId: string) =>
      client.post<{ ttlSec: number; resendAfterSec: number }>(
        `/app/v1/withdrawals/intents/${encodeURIComponent(intentId)}/otp/email`,
        {},
      ),
    getMerchantAutoWithdrawal: (merchantId: string) =>
      client.get<Contracts.BalanceDto>(`/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/balances`),
    verifyAutoRule: (merchantId: string, ruleId: string, payload: Contracts.VerifyAutoRulePayload) =>
      client.post(
        `/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/withdrawals/auto/rules/${encodeURIComponent(ruleId)}/verify`,
        payload,
      ),
    getMerchantWithdrawalAddresses: (merchantId: string) =>
      client.get<WithdrawalAddressState>(
        `/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/security/withdrawal-addresses`,
      ),
    addMerchantWithdrawalAddress: (
      merchantId: string,
      payload: { network: string; address: string; label?: string; reason: string; secondFactor: SecondFactor },
    ) =>
      client.post(
        `/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/security/withdrawal-addresses`,
        payload,
      ),
    deleteMerchantWithdrawalAddress: (
      merchantId: string,
      entryId: string,
      payload: { reason: string; secondFactor: SecondFactor },
    ) =>
      client.delete(
        `/app/v1/admin/merchants/${encodeURIComponent(merchantId)}/security/withdrawal-addresses/${encodeURIComponent(entryId)}`,
        payload,
      ),
  };
}

export type AccountingService = ReturnType<typeof createAccountingService>;
export type AddressAmlService = ReturnType<typeof createAddressAmlService>;
export type AuthService = ReturnType<typeof createAuthService>;
export type InvoicesService = ReturnType<typeof createInvoicesService>;
export type IpWhitelistService = ReturnType<typeof createIpWhitelistService>;
export type MetaService = ReturnType<typeof createMetaService>;
export type ProjectsService = ReturnType<typeof createProjectsService>;
export type ReferralService = ReturnType<typeof createReferralService>;
export type WalletsService = ReturnType<typeof createWalletsService>;
export type AuditLogService = ReturnType<typeof createAuditLogService>;
export type ReconciliationService = ReturnType<typeof createReconciliationService>;
export { createRbacService } from './rbac-service';
export type WithdrawalsService = ReturnType<typeof createWithdrawalsService>;
export type { SecurityService, RbacService, ComplianceService, AmlService, WorkspaceService };

export interface ApiServices {
  accounting: AccountingService;
  addressAml: AddressAmlService;
  auth: AuthService;
  invoices: InvoicesService;
  ipWhitelist: IpWhitelistService;
  meta: MetaService;
  projects: ProjectsService;
  referral: ReferralService;
  wallets: WalletsService;
  withdrawals: WithdrawalsService;
  auditLog: AuditLogService;
  security: SecurityService;
  rbac: RbacService;
  compliance: ComplianceService;
  aml: AmlService;
  workspace: WorkspaceService;
  reconciliation: ReconciliationService;
}

export function createApiServices(client: ApiClient): ApiServices {
  return {
    accounting: createAccountingService(client),
    addressAml: createAddressAmlService(client),
    auth: createAuthService(client),
    invoices: createInvoicesService(client),
    ipWhitelist: createIpWhitelistService(client),
    meta: createMetaService(client),
    projects: createProjectsService(client),
    referral: createReferralService(client),
    wallets: createWalletsService(client),
    withdrawals: createWithdrawalsService(client),
    auditLog: createAuditLogService(client),
    security: createSecurityService(client),
    rbac: createRbacService(client),
    compliance: createComplianceService(client),
    aml: createAmlService(client),
    workspace: createWorkspaceService(client),
    reconciliation: createReconciliationService(client),
  };
}
